import { type KeyboardEvent, memo, type ReactNode, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'

export interface TabItem {
  id: string
  label: string
  // Marks the tab (and tells screen readers) that a field inside it has an error
  hasError?: boolean
  content: ReactNode
}

interface TabsProps {
  tabs: TabItem[]
  activeId: string
  onChange: (id: string) => void
  // Prefix of the ids that tie each tab to its panel; unique per page
  idPrefix: string
  'aria-label': string
}

// Every panel stays mounted (hidden attribute, so it is out of the accessibility tree): the
// fields keep their state and can be found by id when an error needs to move focus into one
export const Tabs = memo(
  ({ tabs, activeId, onChange, idPrefix, 'aria-label': ariaLabel }: TabsProps) => {
    const { t } = useTranslation('admin-mobifull')
    const buttons = useRef<Record<string, HTMLButtonElement | null>>({})

    const handleKeyDown = useCallback(
      (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
        let next = -1
        if (e.key === 'ArrowRight') {
          next = (index + 1) % tabs.length
        } else if (e.key === 'ArrowLeft') {
          next = (index - 1 + tabs.length) % tabs.length
        } else if (e.key === 'Home') {
          next = 0
        } else if (e.key === 'End') {
          next = tabs.length - 1
        }
        if (next < 0) {
          return
        }
        e.preventDefault()
        onChange(tabs[next].id)
        buttons.current[tabs[next].id]?.focus()
      },
      [tabs, onChange],
    )

    return (
      <div className="tabs">
        <div className="tabs__list" role="tablist" aria-label={ariaLabel}>
          {tabs.map((tab, index) => {
            const selected = tab.id === activeId
            return (
              <button
                key={tab.id}
                ref={el => {
                  buttons.current[tab.id] = el
                }}
                type="button"
                role="tab"
                id={`${idPrefix}-tab-${tab.id}`}
                aria-selected={selected}
                aria-controls={`${idPrefix}-panel-${tab.id}`}
                tabIndex={selected ? 0 : -1}
                className={`tabs__tab${selected ? ' tabs__tab--active' : ''}${tab.hasError ? ' tabs__tab--error' : ''}`}
                onClick={() => onChange(tab.id)}
                onKeyDown={e => handleKeyDown(e, index)}>
                {tab.label}
                {tab.hasError ? (
                  <>
                    <span className="tabs__marker" aria-hidden="true">
                      !
                    </span>
                    <span className="sr-only">{t('common.tabs.hasError')}</span>
                  </>
                ) : null}
              </button>
            )
          })}
        </div>
        {tabs.map(tab => (
          <div
            key={tab.id}
            role="tabpanel"
            id={`${idPrefix}-panel-${tab.id}`}
            aria-labelledby={`${idPrefix}-tab-${tab.id}`}
            className="tabs__panel"
            hidden={tab.id !== activeId}>
            {tab.content}
          </div>
        ))}
      </div>
    )
  },
)

Tabs.displayName = 'Tabs'
